import debounce from "lodash/debounce";
import { offsetRelativeToBody } from "./utilities/dom-measurement";

type Entry = {
    element: HTMLDivElement;
    mutationObserver: MutationObserver;
    resizeObserver: ResizeObserver;
    resizeContainer: () => void;
};

const entries: Entry[] = [];

const className = "smooth-size-container";
const activatedAttributeName = "data-activated";

function catchUninitializedElements() {
    const elements = document.body.querySelectorAll<HTMLDivElement>(
        `div.${className}:not([${activatedAttributeName}=true])`
    );
    elements.forEach(initializeElement);
}

const initializerMutationObserver = new MutationObserver(() => {
    catchUninitializedElements();
});

function* navNodeList(nodeList: NodeList) {
    for (let i = 0; i < nodeList.length; i++) {
        yield nodeList.item(i);
    }
}

function createMutationCallback(entry: Entry): MutationCallback {
    return (mutations) => {
        for (const mutation of mutations) {
            switch (mutation.type) {
                case "childList":
                    for (const addedNode of navNodeList(mutation.addedNodes)) {
                        if (addedNode == null) continue;
                        if (addedNode instanceof Element)
                            entry.resizeObserver.observe(addedNode);
                    }

                    for (const removedNode of navNodeList(
                        mutation.removedNodes
                    )) {
                        if (removedNode == null) continue;
                        if (removedNode instanceof Element)
                            entry.resizeObserver.unobserve(removedNode);
                    }

                    break;
            }
        }
    };
}

function createResizeCallback(entry: Entry): ResizeObserverCallback {
    return () => {
        entry.resizeContainer();
    };
}

function createContainerResizeFunction(entry: Entry) {
    return debounce(() => {
        if ("suspendResize" in window && window.suspendResize == true) return;
        resizeContainer(entry.element);
    }, 500);
}

function getOverScale(el: HTMLElement) {
    const result = {
        top: Math.min(0, el.offsetTop),
        left: Math.min(0, el.offsetLeft),
    };

    const len = el.children.length;
    for (let i = 0; i < len; i++) {
        const subEl = el.children[i];
        if (!(subEl instanceof HTMLElement)) continue;
        const subOverscale = getOverScale(subEl);
        result.top += subOverscale.top;
        result.left += subOverscale.left;
    }

    return result;
}

function getOverSpace(el: HTMLElement) {
    const result = {
        height: 0,
        width: 0,
    };

    const elOffset = offsetRelativeToBody(el);
    //const styleMap = el.computedStyleMap();
    // const topPadding = styleMap.get("padding-top");
    // const leftPadding = styleMap.get("padding-left");

    let highest = 0;
    let widest = 0;

    const len = el.children.length;
    for (let i = 0; i < len; i++) {
        const subEl = el.children[i];
        if (!(subEl instanceof HTMLElement)) continue;
        const subElOffset = offsetRelativeToBody(subEl);
        const width = subElOffset.left - elOffset.left + subEl.offsetWidth;

        if (width > widest) {
            widest = width;
        }

        const height = subElOffset.top - elOffset.top + subEl.offsetHeight;
        if (height > highest) {
            highest = height;
        }
    }

    result.width = Math.max(0, el.offsetWidth - widest);

    // if (leftPadding instanceof CSSUnitValue && leftPadding.unit == "px") {
    //     result.width -= leftPadding.value;
    // }

    result.height = Math.max(0, el.offsetHeight - highest);

    // if (topPadding instanceof CSSUnitValue && topPadding.unit == "px") {
    //     result.height -= topPadding.value;
    // }

    return result;
}

function getPxValue(value: string | undefined) {
    if (value != null) return parsePxValue(value);
    return undefined;
}

function parsePxValue(str: string) {
    if (str.endsWith("px")) {
        return parseFloat(str.substring(0, str.length - 2));
    }
    return undefined;
}

function resizeContainer(el: HTMLDivElement) {
    const overscale = getOverScale(el);
    const overspace = getOverSpace(el);

    const styleMap = getComputedStyle(el);

    const width =
        el.scrollWidth +
        overscale.left -
        overspace.width -
        (getPxValue(styleMap.getPropertyValue("padding-left")) ?? 0);
    const height =
        el.scrollHeight +
        overscale.top -
        overspace.height -
        (getPxValue(styleMap.getPropertyValue("padding-top")) ?? 0);

    // const bounds = el.getBoundingClientRect();
    // const width = bounds.width;
    // const height = bounds.height;
    el.style.width = width + "px";
    el.style.height = height + "px";
}

function checkInitialized(el: HTMLElement) {
    return el.getAttribute(activatedAttributeName) == "true";
}

function initializeElement(el: HTMLDivElement) {
    if (checkInitialized(el)) return;
    el.setAttribute(activatedAttributeName, "true");
    const entry: Entry = {
        element: el,
        mutationObserver: null!,
        resizeObserver: null!,
        resizeContainer: null!,
    };
    entries.push(entry);
    entry.resizeContainer = createContainerResizeFunction(entry);
    (el as any).resizeContainer = () => entry.resizeContainer();
    entry.resizeObserver = new ResizeObserver(createResizeCallback(entry));
    entry.mutationObserver = new MutationObserver(
        createMutationCallback(entry)
    );
    entry.mutationObserver.observe(el, {
        childList: true,
        subtree: false,
    });

    el.style.transition = "width 400ms ease-out, height 400ms ease-out";

    for (const childNode of navNodeList(el.childNodes)) {
        if (childNode == null) continue;
        if (childNode instanceof Element)
            entry.resizeObserver.observe(childNode);
    }
}

// function unloadElement(el: HTMLDivElement) {
//     const entry = entries.find((x) => x.element == el);
//     if (entry == null) return;
//     entries.splice(entries.indexOf(entry), 1);
//     entry.element.removeAttribute(activatedAttributeName);
//     entry.resizeObserver?.disconnect();
//     entry.mutationObserver?.disconnect();
//     delete (el as any).resizeContainer;
// }

function init() {
    window.addEventListener("DOMContentLoaded", catchUninitializedElements);
    window.addEventListener("load", () => {
        catchUninitializedElements();
        initializerMutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["class"],
        });
    });
}

init();
